import { useEffect, useState } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { Flame, CheckCircle2, Clock, PlayCircle, Trophy } from 'lucide-react';
import Badges from '@/components/Badges';
import PushToggle from '@/components/PushToggle';

export default function Profile() {
  const { user, logout } = useAuth();
  const [progress, setProgress] = useState({ items: [], stats: {} });
  const [badges, setBadges] = useState([]);
  const [trophies, setTrophies] = useState([]);

  useEffect(() => {
    (async () => {
      const [p, b, t] = await Promise.all([
        api.get('/progress'),
        api.get('/badges'),
        api.get('/trophies'),
      ]);
      setProgress(p.data);
      setBadges(b.data.badges);
      setTrophies(t.data);
    })();
  }, []);

  const stats = progress.stats || {};

  return (
    <Layout>
      <div className="p-6 md:p-10 max-w-3xl">
        <div className="mb-8 rise flex items-center gap-5">
          {user?.picture ? (
            <img src={user.picture} alt="" className="w-20 h-20 rounded-2xl object-cover" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-stone-700" />
          )}
          <div>
            <div className="overline text-orange-400">Profil</div>
            <h1 className="font-display text-5xl tracking-tight">{user?.name}</h1>
            <div className="text-stone-400">{user?.email}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <MiniStat icon={Flame} label="Série" value={`${stats.streak_days || 0} j`} />
          <MiniStat icon={Clock} label="Minutes" value={stats.total_minutes || 0} />
          <MiniStat icon={CheckCircle2} label="Terminées" value={stats.completed_songs || 0} />
          <MiniStat icon={PlayCircle} label="En cours" value={stats.learning_songs || 0} />
        </div>

        {trophies.length > 0 && (
          <div className="mb-10">
            <h2 className="font-display text-3xl mb-4 flex items-center gap-2">
              <Trophy className="text-orange-400" /> Trophées de champion·ne
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {trophies.map((tr, i) => (
                <div key={tr.id} data-testid={`trophy-${i}`} className="p-5 rounded-2xl border border-orange-800/60 bg-gradient-to-br from-orange-950/50 to-transparent flex items-start gap-3">
                  <div className="w-12 h-12 rounded-xl bg-orange-700 flex items-center justify-center shrink-0 shadow-lg shadow-orange-900/40">
                    <Trophy size={24} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="overline text-orange-300">Semaine {tr.week}</div>
                    <div className="font-display text-xl truncate">{tr.song_title}</div>
                    <div className="text-stone-400 text-sm truncate">{tr.song_artist}</div>
                    <div className="text-xs text-stone-500 mt-1">Score {tr.score} · {new Date(tr.awarded_at).toLocaleDateString()}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mb-10">
          <h2 className="font-display text-3xl mb-4">Notifications</h2>
          <div className="card-stone p-5">
            <div className="text-stone-300 text-sm mb-3">Active les notifications push pour recevoir tes rappels de séries et les annonces de défi hebdo — même quand l'app est fermée.</div>
            <PushToggle />
          </div>
        </div>

        <div className="mb-10">
          <h2 className="font-display text-3xl mb-4">Badges</h2>
          <Badges badges={badges} />
        </div>

        <div className="mb-10">
          <h2 className="font-display text-3xl mb-3">Historique récent</h2>
          <div className="card-stone divide-y divide-stone-800">
            {progress.items?.length ? progress.items.map((it, i) => (
              <div key={i} className="p-4 flex items-center justify-between text-sm">
                <div className="text-stone-200">{it.song_id ? `Chanson ${it.song_id.slice(0,6)}` : `Cours ${it.course_id?.slice(0,6)}`}</div>
                <div className="text-stone-400">{it.status} · {it.minutes_practiced || 0} min</div>
              </div>
            )) : (
              <div className="p-6 text-stone-500 text-sm">Aucune activité pour le moment. Va apprendre une chanson !</div>
            )}
          </div>
        </div>

        <button data-testid="profile-logout" onClick={logout} className="btn-ghost">Déconnexion</button>
      </div>
    </Layout>
  );
}

function MiniStat({ icon: Icon, label, value }) {
  return (
    <div className="card-stone p-4">
      <Icon size={16} className="text-orange-400 mb-2" />
      <div className="font-display text-2xl">{value}</div>
      <div className="text-stone-400 text-xs">{label}</div>
    </div>
  );
}
